import React, { useEffect, useMemo, useRef } from "react";
import { Dialog, Spinner } from "@/components/ui";
import { SocialIdentity } from "@/services/bedrock";
import { getErrorMessage, useSocialProfiles, useSocialProfileReviews } from "@/utils";
import { SOCIAL_NETWORK_METADATA } from "@/interfaces";
import { SocialProfileReviewItem } from "./SocialProfileReviewItem";

interface Props {
  open: boolean;
  userId: string | number;
  socialIdentity: SocialIdentity;
  onClose: () => void;
}

export function SocialProfileReviewsModal({ open, userId, socialIdentity, onClose }: Props) {
  const { socialProfiles, refetchProfile } = useSocialProfiles(userId);
  const { clearSocialProfileReviewsCache, ...reviewsQuery } = useSocialProfileReviews(userId, socialIdentity.id, open);
  const currentIdentity = useMemo(
    () =>
      socialProfiles.find(profile => profile.socialIdentity?.id === socialIdentity.id)?.socialIdentity ??
      socialIdentity,
    [socialProfiles, socialIdentity]
  );
  const previousReviewPollingStatusRef = useRef(currentIdentity.reviewPollingStatus);

  useEffect(() => {
    if (!open || !currentIdentity.socialNetworkProfileId) return;

    refetchProfile(currentIdentity.socialNetworkProfileId).catch(console.error);

    if (currentIdentity.reviewPollingStatus !== "REQUESTED") return;

    const interval = setInterval(() => {
      refetchProfile(currentIdentity.socialNetworkProfileId!).catch(console.error);
    }, 3000);

    return () => clearInterval(interval);
  }, [open, currentIdentity.reviewPollingStatus, refetchProfile, currentIdentity.socialNetworkProfileId]);

  useEffect(() => {
    if (previousReviewPollingStatusRef.current === "REQUESTED" && currentIdentity.reviewPollingStatus !== "REQUESTED") {
      clearSocialProfileReviewsCache();
    }
    previousReviewPollingStatusRef.current = currentIdentity.reviewPollingStatus;
  }, [currentIdentity.reviewPollingStatus, clearSocialProfileReviewsCache]);

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = reviewsQuery;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const reviews = useMemo(() => data?.pages.flatMap(page => page.content) ?? [], [data]);
  const reviewCount = data?.pages[0]?.pagination?.totalCount;
  let isRequestingReviews = true;
  if ((reviewCount || reviewCount === 0) && reviewCount === currentIdentity.reviewCount) {
    isRequestingReviews = false;
  } else if (currentIdentity.reviewPollingStatus !== "REQUESTED") {
    isRequestingReviews = false;
  }

  const platform = SOCIAL_NETWORK_METADATA[currentIdentity.socialNetworkId];

  useEffect(() => {
    if (!open || !hasNextPage || isFetchingNextPage || isRequestingReviews) return;

    const sentinel = loadMoreRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { root, rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, isRequestingReviews, fetchNextPage, reviews.length]);

  const content = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-8 text-primary" />
        </div>
      );
    }
    if (isError) {
      return <p className="text-sm text-destructive py-4">{getErrorMessage(error) || "Failed to load reviews."}</p>;
    }
    if (isRequestingReviews) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner className="size-8 text-primary" />
          <p className="text-sm text-muted-foreground py-4 text-center">Requesting reviews...</p>
        </div>
      );
    }
    if (reviews.length) {
      return (
        <div className="flex flex-col gap-4">
          {reviews.map(review => (
            <SocialProfileReviewItem
              key={review.id}
              review={review}
              userId={userId}
              socialIdentityId={currentIdentity.id}
            />
          ))}
          {hasNextPage && (
            <div ref={loadMoreRef} className="flex items-center justify-center py-4">
              {isFetchingNextPage && <Spinner className="size-6 text-primary" />}
            </div>
          )}
        </div>
      );
    }
    return <p className="text-sm text-muted-foreground py-4 text-center">No reviews yet.</p>;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
      size="2xl"
      showCloseButton
    >
      <Dialog.Header>
        <Dialog.Title>
          <div className="flex flex-row items-center gap-2 justify-center">
            {platform?.icon && <img src={platform.icon} alt={platform.name} width={20} height={20} />}
            <span>{platform?.name} Reviews</span>
          </div>
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.Content ref={scrollContainerRef} className="max-h-[60vh] overflow-y-auto">
        {content()}
      </Dialog.Content>
      <Dialog.Footer showCloseButton />
    </Dialog>
  );
}
